import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, PackagePlus, PackageOpen, Package, IndianRupee,
  BarChart3, Building2, Users, LogOut, Boxes, ScanLine, Truck, Bell, LayoutGrid,
  BookUser, ShieldCheck, Settings, ArrowLeftRight, Smartphone, Globe,
  Menu, X, Mail, ChevronDown, ChevronRight, Database, Wallet, ClipboardList, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationsBell from "@/components/NotificationsBell";
import ThemeToggle from "@/components/ThemeToggle";
import InstallAppButton from "@/components/InstallAppButton";
import { fileUrl } from "@/api/client";
import { PlanBadge } from "@/components/Gated";

const navByRole = (role) => {
  const base = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/scan", label: "Scan (Mobile)", icon: Smartphone, testid: "nav-scan" },
    { to: "/app/inward-log", label: "Inward Log", icon: PackagePlus, testid: "nav-inward-log" },
    { to: "/app/inward", label: "Inward Entry", icon: PackagePlus, testid: "nav-inward" },
    { to: "/app/outward-log", label: "Outward Log", icon: PackageOpen, testid: "nav-outward-log" },
    { to: "/app/outward", label: "Outward Entry", icon: PackageOpen, testid: "nav-outward" },
    { to: "/app/drop", label: "Drop Routes", icon: Truck, testid: "nav-drop" },
    { to: "/app/transfer", label: "Branch Transfer", icon: ArrowLeftRight, testid: "nav-transfer" },
    { to: "/app/pickups", label: "Pickups", icon: Truck, testid: "nav-pickups" },
    { to: "/app/bookings", label: "Bookings", icon: BookUser, testid: "nav-bookings" },
    { to: "/app/parcels", label: "Parcels", icon: Package, testid: "nav-parcels" },
    { to: "/app/rates", label: "Rate Compare", icon: IndianRupee, testid: "nav-rates" },
    { to: "/app/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
    { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
    { to: "/app/reports/rto", label: "RTO Report", icon: RotateCcw, testid: "nav-reports-rto" },
  ];
  const audit = { to: "/app/audit", label: "Audit Log", icon: ShieldCheck, testid: "nav-audit" };
  const settings = { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" };
  const mailLogs = { to: "/app/email-logs", label: "Mail Logs", icon: Mail, testid: "nav-mail-logs" };

  // Consolidated Master menu — replaces the flat nav entries for these modules.
  const masterMenu = {
    group: "Master", icon: Database, testid: "nav-master",
    children: [
      { to: "/app/branches", label: "Branches", icon: Boxes, testid: "nav-master-branches" },
      { to: "/app/locations", label: "Locations", icon: Globe, testid: "nav-master-locations" },
      { to: "/app/employees", label: "Employees", icon: BookUser, testid: "nav-master-employees" },
      { to: "/app/couriers", label: "Couriers", icon: Truck, testid: "nav-master-couriers" },
      { to: "/app/rate-matrix", label: "Courier Rate Matrix", icon: IndianRupee, testid: "nav-master-rate-matrix" },
      { to: "/app/master/cost-centers", label: "Cost Centres", icon: Wallet, testid: "nav-master-cc" },
      { to: "/app/master/companies", label: "Companies", icon: Building2, testid: "nav-master-companies" },
      { to: "/app/master/departments", label: "Departments", icon: LayoutGrid, testid: "nav-master-departments" },
      { to: "/app/master/regulators", label: "Regulators", icon: ShieldCheck, testid: "nav-master-regulators" },
      { to: "/app/master/routes", label: "Routes", icon: Truck, testid: "nav-master-routes" },
      { to: "/app/master/clients-vendors", label: "Clients / Vendors", icon: Users, testid: "nav-master-cv" },
    ],
  };

  if (role === "super_admin") return [base[0],
    { to: "/app/tenants", label: "Tenants", icon: Building2, testid: "nav-tenants" },
    ...base.slice(1),
    { to: "/app/users", label: "Users", icon: Users, testid: "nav-users" },
    masterMenu, mailLogs, audit,
    { to: "/app/platform-admin", label: "Platform Admin", icon: ShieldCheck, testid: "nav-platform-admin" },
    settings];
  if (role === "company_admin") return [...base,
    { to: "/app/users", label: "Team", icon: Users, testid: "nav-users" },
    masterMenu, mailLogs, audit, settings,
  ];
  // Company Executive · Feb 13, 2026 — Full operator ONE LEVEL BELOW company_admin.
  // Gets: base ops (Inward/Outward/Bookings/Parcels/Reports) + Users + a slim Master menu
  // (Couriers, Routes, Locations, Regulators). Explicitly denied: Settings, Rate Matrix (billing),
  // Cost Centres, Companies, Departments, Employees master, Audit Log, Mail Logs.
  if (role === "company_executive") return [...base,
    { to: "/app/users", label: "Team", icon: Users, testid: "nav-users" },
    { group: "Master", icon: Database, testid: "nav-master",
      children: [
        { to: "/app/locations", label: "Locations", icon: Globe, testid: "nav-master-locations" },
        { to: "/app/couriers", label: "Couriers", icon: Truck, testid: "nav-master-couriers" },
        { to: "/app/master/regulators", label: "Regulators", icon: ShieldCheck, testid: "nav-master-regulators" },
        { to: "/app/master/routes", label: "Routes", icon: Truck, testid: "nav-master-routes" },
      ],
    },
  ];
  // Delivery Staff (Runner) · Feb 8, 2026 — Lockdown: side nav shows ONLY "My Deliveries".
  if (role === "delivery_staff") return [
    { to: "/app/drop", label: "My Deliveries", icon: Truck, testid: "nav-drop" },
  ];
  // Route Moderator · Feb 10, 2026 — sees only parcels on their routes; manages outward handoff.
  if (role === "route_moderator") return [
    base[0], // Dashboard
    { to: "/app/parcels", label: "Route Parcels", icon: Boxes, testid: "nav-parcels" },
    { to: "/app/outward-log", label: "Outward Log", icon: ScanLine, testid: "nav-outward-log" },
    { to: "/app/outward", label: "Outward Entry", icon: Truck, testid: "nav-outward" },
  ];
  if (role === "branch_admin") return [...base,
    masterMenu,
    { to: "/app/users", label: "Branch Team", icon: Users, testid: "nav-users" },
  ];
// Hide "Master" module completely for Branch Users (RBAC · Branch-level Data Security).
  if (role === "branch_user") return [...base];
  return [...base, masterMenu];
};

/** Expandable "Master" group in the sidebar. Auto-opens when a child route is active. */
function MasterNavGroup({ node, location }) {
  const activePath = location?.pathname || "";
  const anyChildActive = node.children.some((c) => activePath.startsWith(c.to));
  const [open, setOpen] = useState(anyChildActive);
  useEffect(() => { if (anyChildActive) setOpen(true); }, [anyChildActive]);
  return (
    <div data-testid={node.testid} className="space-y-0.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${node.testid}-toggle`}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
          anyChildActive
            ? "bg-white/15 text-white font-semibold"
            : "text-white/85 hover:text-white hover:bg-white/10"
        }`}
      >
        <node.icon size={16} strokeWidth={2} />
        <span className="flex-1 text-left">{node.group}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="ml-2 pl-2 border-l border-white/15 space-y-0.5" data-testid={`${node.testid}-children`}>
          {node.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-[13px] rounded-md transition-colors ${
                  isActive
                    ? "bg-white text-primary font-semibold shadow-sm"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <c.icon size={14} strokeWidth={2} />
              <span>{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// Bottom-tab items (mobile only) — most-used flows for one-thumb reach
const bottomTabsByRole = (role) => {
  if (role === "super_admin") {
    return [
      { to: "/app/dashboard", label: "Home", icon: LayoutDashboard, testid: "bottom-dashboard" },
      { to: "/app/tenants",   label: "Tenants", icon: Building2,   testid: "bottom-tenants" },
      { to: "/app/parcels",   label: "Parcels", icon: Package,     testid: "bottom-parcels" },
      { to: "/app/users",     label: "Users",   icon: Users,       testid: "bottom-users" },
    ];
  }
  return [
    { to: "/app/dashboard", label: "Home",    icon: LayoutDashboard, testid: "bottom-dashboard" },
    { to: "/app/scan",      label: "Scan",    icon: Smartphone,      testid: "bottom-scan" },
    { to: "/app/inward",    label: "Inward",  icon: PackagePlus,     testid: "bottom-inward" },
    { to: "/app/parcels",   label: "Parcels", icon: Package,         testid: "bottom-parcels" },
  ];
};

export default function AppLayout() {
  const { user, tenant, branch, platform, logout } = useAuth();
  const brandName = platform?.brand_name || "Mailroom.OS";
  const platformLogo = platform?.logo_url;
  const navigate = useNavigate();
  const location = useLocation();
  const nav = navByRole(user?.role);
  const bottomTabs = bottomTabsByRole(user?.role);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close drawer when route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const SidebarContent = (
    <>
      <div className="px-5 py-5 border-b border-white/15">
        <div className="flex items-center gap-3">
          {(tenant?.logo_url || platformLogo) ? (
            <div className="h-9 max-w-[96px] flex items-center justify-center shrink-0" data-testid="sidebar-logo">
              <img
                src={fileUrl(tenant?.logo_url || platformLogo)}
                alt={tenant?.logo_url ? "Company logo" : `${brandName} logo`}
                className="block h-full w-auto max-w-full object-contain"
              />
            </div>
          ) : (
            <div className="w-10 h-10 bg-white text-primary flex items-center justify-center font-display font-bold text-lg rounded-md shadow-sm overflow-hidden shrink-0" data-testid="sidebar-logo">
              <span>{brandName.charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-display text-[15px] leading-tight tracking-tight text-white break-words" title={tenant?.name || brandName}>
              {tenant?.name ? <span>{tenant.name}</span> : <span>{brandName}</span>}
            </div>
            <div className="mt-1.5 text-[10px] uppercase tracking-[0.14em] text-white/60 font-semibold truncate">Enterprise mailroom</div>
          </div>
          <button
            type="button"
            data-testid="drawer-close"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden text-white/80 hover:text-white p-1.5 -mr-1 rounded"
            aria-label="Close menu"
          ><X size={18} /></button>
        </div>
      </div>

      <nav
        className="flex-1 min-h-0 py-3 px-3 space-y-0.5 overflow-y-auto overscroll-contain"
        data-testid="sidebar-nav"
      >
        {nav.map((n) => n.group ? (
          <MasterNavGroup key={n.group} node={n} location={location} />
        ) : (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            end={n.exact === true || n.to === "/app/reports"}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                isActive
                  ? "bg-white text-primary font-semibold shadow-sm"
                  : "text-white/85 hover:text-white hover:bg-white/10"
              }`
            }
          >
            <n.icon size={16} strokeWidth={2} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Sign out first · then logged-in user details below · both pinned to sidebar bottom */}
      <div className="border-t border-white/15 px-3 py-3 shrink-0">
        <button
          type="button"
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-white/95 hover:text-primary hover:bg-white transition-colors border border-white/25"
        >
          <LogOut size={16} strokeWidth={2} />
          <span>Sign out</span>
        </button>
      </div>

      <div className="border-t border-white/15 px-4 py-3 text-xs shrink-0" data-testid="sidebar-userinfo">
        <div className="flex items-center gap-2 mb-1">
          <Truck size={12} className="text-white/70" />
          <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-white/70">
            {tenant?.code || (user?.role === "super_admin" ? "PLATFORM" : "—")}
          </span>
        </div>
        <div className="font-semibold text-sm text-white truncate" title={user?.name}>{user?.name}</div>
        <div className="text-white/60 truncate mono text-[10px]" title={user?.email}>{user?.email}</div>
        <div className="text-white/60 uppercase tracking-widest text-[10px] mt-0.5 flex items-center gap-1.5">
          <span>{user?.role?.replace("_", " ")}</span>
          <PlanBadge compact />
        </div>
        {branch && <div className="text-white/60 text-[11px] mt-0.5 truncate" title={branch.location || branch.name}>{branch.name}{branch.location ? ` · ${branch.location}` : ""}</div>}
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar (permanent, ≥ lg) — h-screen + sticky so the internal nav scroll actually kicks in */}
      <aside
        className="hidden lg:flex w-[260px] flex-col bg-primary text-primary-foreground shadow-[4px_0_16px_-8px_rgba(190,30,30,0.35)] h-screen sticky top-0 self-start"
        data-testid="app-sidebar"
      >{SidebarContent}</aside>

      {/* Mobile drawer (< lg) */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-foreground/60 backdrop-blur-[2px]"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-overlay"
        />
      )}
      <aside
        className={`lg:hidden fixed top-0 bottom-0 left-0 z-50 w-[82%] max-w-[300px] bg-primary text-primary-foreground flex flex-col transition-transform duration-200 shadow-2xl ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="app-sidebar-mobile"
      >{SidebarContent}</aside>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden bg-white pb-16 lg:pb-0" data-testid="app-main">
        {/* Top bar */}
        <div className="flex items-center gap-2 px-3 sm:px-6 py-2.5 bg-primary text-primary-foreground sticky top-0 z-20 shadow-[0_2px_12px_-4px_rgba(190,30,30,0.4)]" data-testid="topbar">
          {/* Mobile hamburger */}
          <button
            type="button"
            data-testid="drawer-open"
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden bg-white/10 hover:bg-white/20 text-white p-2 rounded-md"
            aria-label="Open menu"
          ><Menu size={18} /></button>

          <div className="flex-1 min-w-0 flex items-center gap-2 text-white/95">
            <span className="hidden md:inline text-[11px] uppercase tracking-[0.18em] font-semibold text-white/70">Mailroom Operations</span>
            <span className="hidden md:inline text-white/40">/</span>
            <span className="text-sm font-medium truncate">{tenant?.name || "Platform Console"}</span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Install button — icon only on mobile */}
            <div className="hidden sm:inline-flex"><InstallAppButton testid="topbar-install-btn" compact /></div>
            <a
              href="/track"
              target="_blank"
              rel="noreferrer"
              data-testid="topbar-public-track"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-white/25 bg-white/10 hover:bg-white hover:text-primary text-white px-2.5 sm:px-3 py-1.5 rounded-md transition-colors"
              title="Public tracking portal"
            >
              <Globe size={13} />
              <span className="hidden md:inline">Public Tracker</span>
            </a>
            <div className="hidden sm:inline-flex"><ThemeToggle /></div>
            <NotificationsBell />
          </div>
        </div>

        <Outlet />
      </main>

      {/* Mobile bottom-tab bar (thumb-friendly) */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-border shadow-[0_-4px_14px_-6px_rgba(15,23,42,0.15)]"
        data-testid="bottom-tabs"
      >
        <div className="flex items-stretch justify-around">
          {bottomTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <t.icon size={20} strokeWidth={isActive ? 2.5 : 2} className="mb-0.5" />
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
